/**
 * /accounts — list every configured S3-compatible account, create new ones,
 * test connectivity, and edit / delete existing. API-backed.
 */

import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ApiError, api } from '../lib/api';
import { Badge, Banner, Icon, Spinner } from '../components/brand';

interface Account {
  id: string;
  name: string;
  provider: string;
  endpoint: string | null;
  region: string;
  forcePathStyle: boolean;
  cfAccountId: string | null;
  hasCfToken: boolean;
  createdAt: number;
  updatedAt: number;
}

interface AccountInput {
  name: string;
  provider: string;
  endpoint: string;
  region: string;
  forcePathStyle: boolean;
  accessKeyId: string;
  secretAccessKey: string;
  cfAccountId: string;
  cfApiToken: string;
}

const PROVIDERS = ['r2', 's3', 'minio', 'b2', 'wasabi', 'custom'] as const;

const PROVIDER_BADGE: Record<string, 'accent' | 'info' | 'success' | 'warn' | 'neutral'> = {
  r2: 'accent',
  s3: 'info',
  minio: 'success',
  b2: 'warn',
  wasabi: 'neutral',
  custom: 'neutral',
};

const blankInput = (): AccountInput => ({
  name: '',
  provider: 'r2',
  endpoint: '',
  region: 'auto',
  forcePathStyle: false,
  accessKeyId: '',
  secretAccessKey: '',
  cfAccountId: '',
  cfApiToken: '',
});

export function Accounts() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const {
    data: accounts = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<Account[]>('/api/accounts'),
  });

  const createMutation = useMutation({
    mutationFn: (input: AccountInput) => {
      const body = {
        name: input.name.trim(),
        provider: input.provider,
        region: input.region.trim() || 'auto',
        forcePathStyle: input.forcePathStyle,
        accessKeyId: input.accessKeyId,
        secretAccessKey: input.secretAccessKey,
        ...(input.endpoint ? { endpoint: input.endpoint.trim() } : {}),
        ...(input.cfAccountId ? { cfAccountId: input.cfAccountId.trim() } : {}),
        ...(input.cfApiToken ? { cfApiToken: input.cfApiToken } : {}),
      };
      return api.post<Account>('/api/accounts', body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounts'] });
      setCreating(false);
    },
  });

  return (
    <div data-screen-label="03 Accounts">
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          marginBottom: 24,
        }}
      >
        <div>
          <h1 className="h-page">Accounts</h1>
          <p className="muted" style={{ marginTop: 6 }}>
            {isLoading ? 'Loading…' : `${accounts.length} configured`} · S3-compatible endpoints
          </p>
        </div>
        <button
          type="button"
          className="bm-btn bm-btn-primary"
          onClick={() => {
            setCreating((v) => !v);
            setEditingId(null);
          }}
        >
          <Icon.plus size={13} /> {creating ? 'Cancel' : 'New account'}
        </button>
      </div>

      {creating && (
        <div className="bm-card" style={{ marginBottom: 20, padding: 24 }}>
          <div className="bm-row" style={{ marginBottom: 14 }}>
            <span className="h-eyebrow">New account</span>
            <span className="bm-spacer" />
            <span className="muted" style={{ fontSize: 12 }}>
              credentials are encrypted at rest
            </span>
          </div>
          <AccountForm
            mode="create"
            initial={blankInput()}
            submitLabel="Create account"
            pending={createMutation.isPending}
            error={createMutation.error as ApiError | null}
            onCancel={() => setCreating(false)}
            onSubmit={(input) => createMutation.mutate(input)}
          />
        </div>
      )}

      {error && (
        <div style={{ marginBottom: 16 }}>
          <Banner variant="danger" code={(error as ApiError).code ?? 'ERROR'}>
            {(error as Error).message}
          </Banner>
        </div>
      )}

      {!isLoading && accounts.length === 0 && !creating ? (
        <div className="bm-card bm-empty">
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 999,
              background: 'var(--bm-tint-2)',
              color: 'var(--bm-text-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon.accounts size={22} />
          </div>
          <h3>No accounts yet</h3>
          <p className="muted" style={{ fontSize: 13, maxWidth: 320 }}>
            Add an S3-compatible store to start managing buckets and objects from here.
          </p>
          <button type="button" className="bm-btn bm-btn-primary" onClick={() => setCreating(true)}>
            <Icon.plus size={13} /> New account
          </button>
        </div>
      ) : (
        <div className="bm-col bm-gap-12">
          {accounts.map((acc) =>
            editingId === acc.id ? (
              <EditCard key={acc.id} account={acc} onClose={() => setEditingId(null)} />
            ) : (
              <AccountCard
                key={acc.id}
                account={acc}
                onEdit={() => {
                  setEditingId(acc.id);
                  setCreating(false);
                }}
              />
            ),
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Card ─────────────────────────────────────────────────────────── */

function AccountCard({ account, onEdit }: { account: Account; onEdit: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [testResult, setTestResult] = useState<string | null>(null);

  const test = useMutation({
    mutationFn: () =>
      api.post<{ buckets: string[]; latencyMs: number }>(`/api/accounts/${account.id}/test`),
    onSuccess: (data) =>
      setTestResult(`200 OK · ${data.latencyMs}ms · ${data.buckets.length} buckets visible`),
    onError: (e) => setTestResult(`FAIL · ${(e as ApiError).message}`),
  });

  const del = useMutation({
    mutationFn: () => api.delete<{ id: string }>(`/api/accounts/${account.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts'] }),
  });

  return (
    <div className="bm-card" style={{ padding: 0 }}>
      <div style={{ padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="bm-row bm-gap-8" style={{ flexWrap: 'wrap' }}>
            <h3 className="h-card" style={{ whiteSpace: 'nowrap' }}>
              {account.name}
            </h3>
            <Badge variant={PROVIDER_BADGE[account.provider] ?? 'neutral'}>
              {account.provider.toUpperCase()}
            </Badge>
          </div>
          <div className="bm-row" style={{ marginTop: 6, gap: 10, flexWrap: 'wrap' }}>
            {account.endpoint && (
              <span
                className="mono"
                style={{
                  fontSize: 12,
                  color: 'var(--bm-text-soft)',
                  whiteSpace: 'nowrap',
                }}
              >
                {account.endpoint}
              </span>
            )}
            <span className="muted" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
              · {account.region}
            </span>
          </div>
          {testResult && (
            <div
              className="mono"
              style={{
                fontSize: 12,
                color: testResult.startsWith('200') ? 'var(--bm-success)' : 'var(--bm-danger)',
                marginTop: 8,
              }}
            >
              <Icon.check size={10} /> {testResult}
            </div>
          )}
        </div>
        <div className="bm-row bm-gap-8" style={{ flexShrink: 0 }}>
          <button
            type="button"
            className="bm-btn bm-btn-primary bm-btn-sm"
            onClick={() => navigate(`/accounts/${account.id}/buckets`)}
          >
            <Icon.bucket size={12} /> Buckets
          </button>
          <button
            type="button"
            className="bm-btn bm-btn-ghost bm-btn-sm"
            disabled={test.isPending}
            onClick={() => test.mutate()}
          >
            {test.isPending ? <Spinner size={11} /> : <Icon.refresh size={12} />}{' '}
            {test.isPending ? 'testing…' : 'Test'}
          </button>
          <button type="button" className="bm-btn bm-btn-ghost bm-btn-sm" onClick={onEdit}>
            <Icon.edit size={12} /> Edit
          </button>
          <button
            type="button"
            className="bm-btn bm-btn-danger bm-btn-sm"
            disabled={del.isPending}
            onClick={() => {
              if (
                window.confirm(
                  `Delete account "${account.name}"? Buckets and upload sessions cascade.`,
                )
              ) {
                del.mutate();
              }
            }}
          >
            <Icon.trash size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}

function EditCard({ account, onClose }: { account: Account; onClose: () => void }) {
  const qc = useQueryClient();

  const initial: AccountInput = {
    name: account.name,
    provider: account.provider,
    endpoint: account.endpoint ?? '',
    region: account.region,
    forcePathStyle: account.forcePathStyle,
    accessKeyId: '',
    secretAccessKey: '',
    cfAccountId: account.cfAccountId ?? '',
    cfApiToken: '',
  };

  const update = useMutation({
    mutationFn: (input: AccountInput) => {
      const body: Record<string, unknown> = {
        name: input.name.trim(),
        endpoint: input.endpoint.trim() || null,
        region: input.region.trim() || 'auto',
        forcePathStyle: input.forcePathStyle,
        cfAccountId: input.cfAccountId.trim() || null,
      };
      if (input.accessKeyId && input.secretAccessKey) {
        body.accessKeyId = input.accessKeyId;
        body.secretAccessKey = input.secretAccessKey;
        if (input.cfApiToken) body.cfApiToken = input.cfApiToken;
      }
      return api.patch<Account>(`/api/accounts/${account.id}`, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounts'] });
      onClose();
    },
  });

  return (
    <div className="bm-card" style={{ padding: 24 }}>
      <div className="bm-row" style={{ marginBottom: 14 }}>
        <span className="h-eyebrow">Edit · {account.name}</span>
        <span className="bm-spacer" />
        <span className="muted" style={{ fontSize: 12 }}>
          leave access/secret blank to keep existing creds
        </span>
      </div>
      <AccountForm
        mode="edit"
        initial={initial}
        submitLabel="Save changes"
        pending={update.isPending}
        error={update.error as ApiError | null}
        onCancel={onClose}
        onSubmit={(input) => update.mutate(input)}
      />
    </div>
  );
}

/* ─── Form ─────────────────────────────────────────────────────────── */

interface FormProps {
  mode: 'create' | 'edit';
  initial: AccountInput;
  submitLabel: string;
  pending: boolean;
  error: ApiError | null;
  onSubmit: (input: AccountInput) => void;
  onCancel: () => void;
}

function AccountForm({
  mode,
  initial,
  submitLabel,
  pending,
  error,
  onSubmit,
  onCancel,
}: FormProps) {
  const [state, setState] = useState<AccountInput>(initial);
  const [advanced, setAdvanced] = useState(false);

  function handle<K extends keyof AccountInput>(key: K, value: AccountInput[K]) {
    setState((s) => ({ ...s, [key]: value }));
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit(state);
  }

  return (
    <form className="bm-col bm-gap-16" onSubmit={submit}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <label className="bm-field">
          <span className="bm-field-label">Name</span>
          <input
            className="bm-input"
            value={state.name}
            onChange={(e) => handle('name', e.target.value)}
            placeholder="r2-prod"
            required
            maxLength={64}
            disabled={mode === 'edit'}
          />
        </label>
        <div className="bm-field">
          <span className="bm-field-label">Provider</span>
          <div className="bm-segmented" style={{ display: 'flex' }}>
            {PROVIDERS.map((p) => (
              <button
                type="button"
                key={p}
                data-active={state.provider === p}
                onClick={() => handle('provider', p)}
                style={{ flex: 1 }}
                disabled={mode === 'edit'}
              >
                {p.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>

      <label className="bm-field">
        <span className="bm-field-label">Endpoint</span>
        <input
          className="bm-input mono"
          value={state.endpoint}
          onChange={(e) => handle('endpoint', e.target.value)}
          placeholder="https://…r2.cloudflarestorage.com (blank = AWS default)"
        />
      </label>

      <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr 1fr', gap: 14 }}>
        <label className="bm-field">
          <span className="bm-field-label">Region</span>
          <input
            className="bm-input mono"
            value={state.region}
            onChange={(e) => handle('region', e.target.value)}
            required
          />
        </label>
        <label className="bm-field">
          <span className="bm-field-label">
            {mode === 'edit' ? 'New access key (optional)' : 'Access key'}
          </span>
          <input
            className="bm-input mono"
            value={state.accessKeyId}
            onChange={(e) => handle('accessKeyId', e.target.value)}
            placeholder="AK…"
            autoComplete="off"
            required={mode === 'create'}
          />
        </label>
        <label className="bm-field">
          <span className="bm-field-label">
            {mode === 'edit' ? 'New secret key (optional)' : 'Secret key'}
          </span>
          <input
            type="password"
            className="bm-input mono"
            value={state.secretAccessKey}
            onChange={(e) => handle('secretAccessKey', e.target.value)}
            placeholder={mode === 'edit' ? 'rotate to change' : ''}
            autoComplete="new-password"
            required={mode === 'create'}
          />
        </label>
      </div>

      <div className="bm-row bm-gap-8" style={{ padding: '4px 0' }}>
        <button
          type="button"
          className="bm-row bm-gap-8"
          style={{
            fontSize: 12.5,
            cursor: 'pointer',
            background: 'transparent',
            border: 0,
            padding: 0,
          }}
          onClick={() => handle('forcePathStyle', !state.forcePathStyle)}
        >
          <span className="bm-toggle" data-on={state.forcePathStyle} />
          <span>Force path-style</span>
        </button>
        <span className="muted" style={{ fontSize: 12 }}>
          (required for MinIO / B2)
        </span>
      </div>

      <div>
        <button
          type="button"
          className="bm-btn bm-btn-sm"
          style={{ color: 'var(--bm-text-muted)', padding: 0 }}
          onClick={() => setAdvanced((v) => !v)}
        >
          <Icon.chevronRight
            size={11}
            style={{
              transform: advanced ? 'rotate(90deg)' : 'none',
              transition: 'transform 140ms',
            }}
          />
          R2 advanced (account id + api token)
        </button>
        {advanced && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 12 }}>
            <label className="bm-field">
              <span className="bm-field-label">Cloudflare account id</span>
              <input
                className="bm-input mono"
                value={state.cfAccountId}
                onChange={(e) => handle('cfAccountId', e.target.value)}
              />
            </label>
            <label className="bm-field">
              <span className="bm-field-label">API token</span>
              <input
                type="password"
                className="bm-input mono"
                value={state.cfApiToken}
                onChange={(e) => handle('cfApiToken', e.target.value)}
                autoComplete="new-password"
              />
            </label>
          </div>
        )}
      </div>

      {error && (
        <Banner variant="danger" code={error.code ?? 'ERROR'}>
          {error.message}
        </Banner>
      )}

      <div className="bm-row bm-gap-8" style={{ justifyContent: 'flex-end' }}>
        <button
          type="button"
          className="bm-btn bm-btn-sm"
          onClick={onCancel}
          disabled={pending}
          style={{ color: 'var(--bm-text-muted)' }}
        >
          Cancel
        </button>
        <button type="submit" className="bm-btn bm-btn-primary bm-btn-sm" disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
